/* eslint-disable no-useless-computed-key */
import Vue from 'vue'
import { normalize, schema } from 'normalizr'
import {
  RECEIVE_CUSTOMER,
  RECEIVE_ALL_CUSTOMER,
  RECEIVE_CUSTOMER_METADATA,
  RECEIVING_CUSTOMER,
  SAVING_CUSTOMER,
  UPDATING_CUSTOMER,
  DELETE_CUSTOMER,
  DELETING_CUSTOMER,
  RECEIVE_ADDRESSES,
  RESET_CUSTOMER_META,
  CLEAR_CUSTOMER_STATE,
  CLEAR_STATE,
  UPDATE_CURRENT_CUSTOMER_PAGE,
  RECEIVE_STATES,
  INCREMENT_CUSTOMER_COUNT
} from '../mutationTypes'
import { checkStatus, getHeaders, parseJSON, buildUrl, buildUrlWithQuery } from '@/utils/api'
import { getBySearchCriteria } from '@/utils/helpers/GetterHelpers/CommonGetterFunctions'
import {
  receiveData,
  deleteData,
  increment,
  receiveMetadata,
  resetMetadata,
  clearLocalState,
  clearState
} from '@/store/modules/helpers/mutationHelpers'

const addressSchema = new schema.Entity('addresses')
const stateSchema = new schema.Entity('states')
const customerSchema = new schema.Entity('customers', {
  address_billing: addressSchema,
  address_contact: addressSchema,
  address_mailing: addressSchema,
  address_selected: addressSchema,
  work_state: stateSchema
})
const customerListSchema = new schema.Array(customerSchema)

const state = {
  data: {},
  all: {},
  cache: {},
  count: null,
  shown: null,
  next: null,
  previous: null,
  currentPage: 1,
  saving: false,
  saveFailure: null,
  receivingData: false,
  receiveDataFailure: false,
  updating: null,
  updateFailure: null,
  deleting: false,
  deleteFailure: null,
  autoCode: null
}

const actions = {
  fetchCustomers (store, isNextSet) {
    const urlToFetchFrom = () => {
      if (isNextSet) {
        const nextUrl = store.state.next
        return buildUrl(null, null, nextUrl)
      }
      const instanceUrl = this.getters.getActiveInstance.url
      return buildUrl('customers', null, instanceUrl)
    }
    this.commit(RECEIVING_CUSTOMER, true)
    return Vue.http.get(urlToFetchFrom(), { headers: getHeaders(this.getters.getToken) })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        this.commit(RECEIVING_CUSTOMER, false)
        if (!r.hasOwnProperty('results')) return Promise.reject()
        const { results, next, previous, count } = r
        const { entities, result } = normalize(results, customerListSchema)
        if (entities && entities.addresses) {
          this.commit(RECEIVE_ADDRESSES, { data: entities.addresses })
        }
        if (entities && entities.states) {
          this.commit(RECEIVE_STATES, { data: entities.addresses })
        }
        this.commit(RECEIVE_CUSTOMER, { data: entities.customers })
        this.commit(RECEIVE_CUSTOMER_METADATA, { next, previous, count, result: result.length })
        return entities
      })
      .catch(e => {
        console.log(e)
        this.commit(RECEIVING_CUSTOMER, false)
        return e
      })
  },
  fetchCustomersByPage (store, page) {
    const { cache } = store.state
    const instanceUrl = this.getters.getActiveInstance.url
    let url = buildUrlWithQuery('customers', { page }, instanceUrl)
    let next = (page + 1) > Math.ceil(state.count / 20)
      ? null
      : buildUrlWithQuery('customers', { page: page + 1 }, instanceUrl)
    let previous = (page - 1) <= 0
      ? null
      : buildUrlWithQuery('customers', { page: page - 1 }, instanceUrl)
    // Check if data is already cached
    const c = Object.values(cache).filter(v => v.page && parseInt(v.page) === parseInt(page))
    if (c && c.length && c.length <= 20) {
      const e = normalize(c, customerListSchema)
      this.commit(RECEIVE_CUSTOMER, { page, data: e.entities.customers })
      this.commit(RECEIVE_CUSTOMER_METADATA, { next, previous, result: e.result.length })
      return e
    }
    // If not, fetch
    this.commit(RECEIVING_CUSTOMER, true)
    return Vue.http.get(url, { headers: getHeaders(this.getters.getToken) })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        this.commit(RECEIVING_CUSTOMER, false)
        if (!r) return Promise.reject()
        const { results, next, previous, count } = r
        const { entities, result } = normalize(results, customerListSchema)
        if (entities && entities.addresses) {
          this.commit(RECEIVE_ADDRESSES, { data: entities.addresses })
        }
        if (entities && entities.states) {
          this.commit(RECEIVE_STATES, { data: entities.addresses })
        }
        this.commit(RECEIVE_CUSTOMER, { page, data: entities.customers })
        this.commit(RECEIVE_CUSTOMER_METADATA, { next, previous, count, result: result.length })
        return entities
      })
      .catch(e => {
        console.log(e)
        this.commit(RECEIVING_CUSTOMER, false)
        return e
      })
  },
  fetchCustomersWithQuery (store, query) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(RECEIVING_CUSTOMER, true)
    return Vue.http.get(buildUrlWithQuery('customers', query, instanceUrl), { headers })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        this.commit(RECEIVING_CUSTOMER, false)
        this.commit(RESET_CUSTOMER_META)
        if (!(r && r.results)) return Promise.reject()
        const { results, next, previous, count } = r
        const { entities, result } = normalize(results, customerListSchema)
        if (entities && entities.addresses) {
          this.commit(RECEIVE_ADDRESSES, { data: entities.addresses })
        }
        if (entities && entities.states) {
          this.commit(RECEIVE_STATES, { data: entities.addresses })
        }
        this.commit(RECEIVE_CUSTOMER, { data: entities.customers })
        this.commit(RECEIVE_CUSTOMER_METADATA, { next, previous, count, result: result.length })
        return { query, count, entities }
      })
      .catch(e => {
        console.log(e)
        this.commit(RECEIVING_CUSTOMER, false)
        return e
      })
  },
  fetchSingleCustomerWithQuery (store, query) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(RECEIVING_CUSTOMER, true)
    return Vue.http.get(buildUrlWithQuery('customers', query, instanceUrl), { headers })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        this.commit(RECEIVING_CUSTOMER, false)
        if (!(r && r.results)) return Promise.reject()
        const { results } = r
        const { entities } = normalize(results, customerListSchema)
        if (entities && entities.addresses) {
          this.commit(RECEIVE_ADDRESSES, { data: entities.addresses })
        }
        if (entities && entities.states) {
          this.commit(RECEIVE_STATES, { data: entities.addresses })
        }
        return { query, entities }
      })
      .catch(e => {
        console.log(e)
        this.commit(RECEIVING_CUSTOMER, false)
        return e
      })
  },
  fetchAllCustomers (store) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(RECEIVING_CUSTOMER, true)
    return Vue.http({ url: buildUrl('customers?meta_choices=True', null, instanceUrl), headers, method: 'options' })
      .then(checkStatus)
      .then(parseJSON)
      .then(response => {
        this.commit(RECEIVING_CUSTOMER, false)
        if (!response.hasOwnProperty('choices')) return Promise.reject('Error fetching customers')
        if (!response || !Array.isArray(response.choices)) return Promise.reject('Error normalizing response')
        this.commit(RECEIVE_ALL_CUSTOMER, { data: response.choices })
        return response.choices
      })
      .catch(e => {
        console.log(e)
        this.commit(RECEIVING_CUSTOMER, false)
        return e
      })
  },
  saveCustomer (store, payload) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(SAVING_CUSTOMER, true)
    return Vue.http.post(buildUrl('customers', null, instanceUrl), payload, { headers })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        this.commit(SAVING_CUSTOMER, false)
        if (!r) return Promise.reject('Error saving customer')
        const { entities } = normalize(r, customerSchema)
        if (entities && entities.addresses) {
          this.commit(RECEIVE_ADDRESSES, { data: entities.addresses })
        }
        if (entities && entities.states) {
          this.commit(RECEIVE_STATES, { data: entities.addresses })
        }
        this.commit(RECEIVE_CUSTOMER, { data: entities.customers })
        this.commit(INCREMENT_CUSTOMER_COUNT, 1)
        return { response: r, entities }
      })
      .catch(e => {
        console.log(e)
        this.commit(SAVING_CUSTOMER, false)
        return e
      })
  },
  updateCustomer (store, payload) {
    const { id, data } = payload
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(UPDATING_CUSTOMER, id)
    return Vue.http.patch(buildUrl(`customers/${id}`, null, instanceUrl), data, { headers })
      .then(r => {
        this.commit(UPDATING_CUSTOMER, null)
        if (r && r.status === 200) {
          const { entities } = normalize(r.body, customerSchema)
          if (entities && entities.addresses) {
            this.commit(RECEIVE_ADDRESSES, { data: entities.addresses })
          }
          if (entities && entities.states) {
            this.commit(RECEIVE_STATES, { data: entities.addresses })
          }
          this.commit(RECEIVE_CUSTOMER, { data: entities.customers })
          return { response: r.body, entities }
        } else {
          return Promise.reject('Failed to update customer')
        }
      })
      .catch(e => {
        console.log(e)
        this.commit(UPDATING_CUSTOMER, null)
        return e
      })
  },
  fetchCustomerAutoCode (store, query) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    return Vue.http.get(buildUrl(`customers/autocode`, query, instanceUrl), { headers })
      .then(r => {
        if (!(r && r.status === 200 && r.body.code)) return Promise.reject()
        this.commit('RECEIVE_CUSTOMER_AUTOCODE', r.body.code)
        return r
      })
      .catch(e => {
        console.log(e)
      })
  },
  clearCustomerState (store, clearCache = false) {
    this.commit(CLEAR_CUSTOMER_STATE, clearCache)
  },
  resetCustomerMeta () {
    return this.commit(RESET_CUSTOMER_META)
  }
}

const getters = {
  getCustomers: (state) => {
    return state.data ? Object.values(state.data).map(v => Object.assign({}, v)) : []
  },
  getCustomerMetadata: (state) => {
    const { shown, count } = state
    if (!shown) return { count: 0, shown: 0 }
    return { count, shown }
  },
  getCustomerById: (state) => (id) => {
    if (state.data && state.data[id]) {
      return state.data[id]
    } else if (state.cache && state.cache[id]) {
      return state.cache[id]
    } else {
      return {}
    }
  },
  getAllCustomers: (state) => {
    return state.all
  },
  getCustomerFetchStatus: (state) => {
    return state.receivingData
  },
  getCustomersBySearchCriteria: (state) => (query) => {
    return getBySearchCriteria(state.data, query)
  },
  getCustomerSaveStatus: (state) => {
    return state.saving
  },
  getCustomerUpdateStatus: (state) => {
    return state.updating
  },
  getCustomerDeleteStatus: (state) => {
    return state.deleting
  },
  getCurrentCustomerPage: (state) => {
    if (state.currentPage) return state.currentPage
  },
  getCustomerAutoCode: (state) => state.autoCode
}

const mutations = {
  [RECEIVE_CUSTOMER] (state, results) {
    receiveData(state, results)
  },
  [RECEIVE_ALL_CUSTOMER] (state, results) {
    const { data } = results
    state.all = data
    return true
  },
  [RECEIVING_CUSTOMER] (state, payload) {
    state.receivingData = payload
  },
  [SAVING_CUSTOMER] (state, payload) {
    state.saving = payload
  },
  [UPDATING_CUSTOMER] (state, payload) {
    state.updating = payload
  },
  [DELETE_CUSTOMER] (state, payload) {
    deleteData(state, payload)
  },
  [DELETING_CUSTOMER] (state, payload) {
    state.deleting = payload
  },
  [INCREMENT_CUSTOMER_COUNT] (state, payload) {
    increment(state, payload)
  },
  [RECEIVE_CUSTOMER_METADATA] (state, payload) {
    receiveMetadata(state, payload)
  },
  [UPDATE_CURRENT_CUSTOMER_PAGE] (state, payload) {
    state.currentPage = payload
  },
  [CLEAR_CUSTOMER_STATE] (state, payload) {
    clearLocalState(state, payload)
  },
  [RESET_CUSTOMER_META] (state) {
    resetMetadata(state)
  },
  [CLEAR_STATE] (state) {
    clearState(state)
  },
  ['RECEIVE_CUSTOMER_AUTOCODE'] (state, payload) {
    state.autoCode = payload
  }
}

export default {
  state,
  actions,
  getters,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/modules/customer.js